/**
 * InteractiveCanvasContainer — Wrapper that adds 5-mode interaction
 * to the existing CanvasExplorer. Falls through to plain CanvasExplorer
 * for templates without interactive config.
 *
 * Renders a compact 2-row header:
 *   Row 1: Template tabs (left) + instance selector (right)
 *   Row 2: Mode tabs (left) + progress ring (right)
 */

import { CANVAS_TEMPLATES, type CanvasTemplate } from '@/components/canvas/canvasTemplates'
import { CanvasExplorer } from '@/components/canvas/CanvasExplorer'
import { CanvasInstanceNavigator } from '@/components/canvas/CanvasInstanceNavigator'
import { cn } from '@/lib/utils'
import {
  CanvasInteractiveProvider,
  useCanvasInteractive,
} from './CanvasInteractiveContext'
import { CanvasModeTabs } from './CanvasModeTabs'
import { GuidedModeView } from './GuidedModeView'
import { ChallengeModeView } from './ChallengeModeView'
import { StoryModeView } from './StoryModeView'

function CompactTemplateTabs() {
  const { selectedTemplate, setSelectedTemplate } = useCanvasInteractive()

  return (
    <div className="flex items-center gap-0 overflow-x-auto overflow-y-hidden">
      {CANVAS_TEMPLATES.map((template: CanvasTemplate) => (
        <button
          key={template.id}
          onClick={() => setSelectedTemplate(template)}
          className={cn(
            'px-3.5 py-2.5 text-[13px] font-medium whitespace-nowrap transition-colors border-b-2 -mb-px',
            selectedTemplate.id === template.id
              ? 'text-primary border-primary bg-warm-50'
              : 'text-text-muted hover:text-text-secondary border-transparent'
          )}
        >
          {template.name}
        </button>
      ))}
    </div>
  )
}

function CompactInstanceSelector() {
  const { selectedTemplate, selectedInstanceUri, setSelectedInstanceUri } = useCanvasInteractive()

  return (
    <CanvasInstanceNavigator
      templateId={selectedTemplate.id}
      selectedInstanceUri={selectedInstanceUri}
      onSelectInstance={(uri, _name) => setSelectedInstanceUri(uri)}
      onCreateNew={() => {/* TODO: wire create dialog */}}
      compact
    />
  )
}

function InteractiveCanvasInner() {
  const { activeMode, hasInteractiveConfig, selectedTemplate, selectedInstanceUri } = useCanvasInteractive()

  // No interactive config → render plain CanvasExplorer
  if (!hasInteractiveConfig) {
    return <CanvasExplorer />
  }

  return (
    <div className="flex flex-col h-full">
      {/* Row 1: Template tabs (left) + instance selector (right) */}
      <div className="flex-shrink-0 flex items-center border-b border-warm-200">
        <CompactTemplateTabs />
        <div className="flex-1" />
        <div className="pr-2">
          <CompactInstanceSelector />
        </div>
      </div>

      {/* Row 2: Mode tabs (left) + progress ring (right) */}
      <CanvasModeTabs />

      {/* Content area */}
      <div className="flex-1 min-h-0 overflow-auto">
        {activeMode === 'canvas' && <CanvasExplorer hideHeader hideAuxiliaryPanels template={selectedTemplate} instanceUri={selectedInstanceUri} />}
        {activeMode === 'guided' && <GuidedModeView />}
        {activeMode === 'challenges' && <ChallengeModeView />}
        {activeMode === 'story' && <StoryModeView />}
      </div>
    </div>
  )
}

export function InteractiveCanvasContainer() {
  return (
    <CanvasInteractiveProvider>
      <InteractiveCanvasInner />
    </CanvasInteractiveProvider>
  )
}
